import { effect, EffectRef, SimpleChange, SimpleChanges, untracked } from '@angular/core';

type SignalReaders = Record<string, () => unknown>;

/**
 * Runs legacy input-change logic from signal inputs while preserving
 * previous/current values and first-change semantics.
 */
export function effectInputChanges(readers: SignalReaders, handler: (changes: SimpleChanges) => void): EffectRef {
  const previous = new Map<string, unknown>();

  return effect(
    () => {
      const current = new Map<string, unknown>();
      const changes: SimpleChanges = {};

      for (const [name, read] of Object.entries(readers)) {
        const value = read();
        current.set(name, value);

        const firstChange = !previous.has(name);
        const previousValue = previous.get(name);
        if (firstChange || !Object.is(previousValue, value)) {
          changes[name] = new SimpleChange(previousValue, value, firstChange);
        }
      }

      if (Object.keys(changes).length > 0) {
        untracked(() => handler(changes));
      }

      // Handlers may normalize writable model signals. Capture their final
      // values so the scheduled follow-up effect does not create a loop.
      for (const [name, read] of Object.entries(readers)) {
        previous.set(name, untracked(read));
      }
    },
    { allowSignalWrites: true }
  );
}
